/**
 * Sales History Panel Component
 * Shows today's sales with totals - similar to EasyAs DAILY TOTALS view
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RefreshCw,
  Receipt,
  Search,
  Eye,
  Printer,
  X,
  Calendar,
  CreditCard,
  Banknote,
  Building2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import { ReceiptModal } from "./ReceiptModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SalesHistoryPanel = ({ onClose }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Reprint via the real ReceiptModal (identical to the point-of-sale receipt).
  const [reprintSale, setReprintSale] = useState(null);
  const [showReprintModal, setShowReprintModal] = useState(false);
  const [totals, setTotals] = useState({
    cash: 0,
    card: 0,
    account: 0,
    eft: 0,
    other: 0,
    total: 0,
    count: 0,
    refunds: 0,
  });

  useEffect(() => {
    fetchTodaysSales();
  }, []);

  const fetchTodaysSales = async (attempt = 1) => {
    if (attempt === 1) setLoading(true);
    try {
      // Get today's date range
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();

      // Fetch sales transactions
      const salesResponse = await axios.get(`${API}/sales/transactions`, {
        params: {
          start_date: startOfDay,
          end_date: endOfDay,
          limit: 500,
        },
        timeout: 15000,  // Fail fast rather than hang the whole UI.
      });

      // Fetch account payments (receipts)
      const paymentsResponse = await axios.get(`${API}/account-payments`, {
        params: {
          start_date: startOfDay,
          end_date: endOfDay,
        },
        timeout: 15000,
      }).catch(() => ({ data: [] })); // If endpoint fails, return empty array

      const salesData = salesResponse.data || [];
      const paymentsData = paymentsResponse.data || [];
      
      // Filter out pos_sale_payment type payments - these are already included 
      // as "Account Payment" line items in the sales, so showing them separately
      // would be double-counting
      const standalonePayments = paymentsData.filter(payment => 
        payment.payment_type !== 'pos_sale_payment'
      );
      
      // Convert account payments to a format similar to sales for display
      const formattedPayments = standalonePayments.map(payment => {
        let payments = [];
        
        // Handle split payments - parse from note field
        if (payment.payment_method === 'SPLIT' && payment.note) {
          // Parse "Cash: R10, Card: R10, EFT: R0" format
          const cashMatch = payment.note.match(/Cash:\s*R?([\d.]+)/i);
          const cardMatch = payment.note.match(/Card:\s*R?([\d.]+)/i);
          const eftMatch = payment.note.match(/EFT:\s*R?([\d.]+)/i);
          
          if (cashMatch && parseFloat(cashMatch[1]) > 0) {
            payments.push({ method: 'CASH', amount: parseFloat(cashMatch[1]) });
          }
          if (cardMatch && parseFloat(cardMatch[1]) > 0) {
            payments.push({ method: 'CARD', amount: parseFloat(cardMatch[1]) });
          }
          if (eftMatch && parseFloat(eftMatch[1]) > 0) {
            payments.push({ method: 'EFT', amount: parseFloat(eftMatch[1]) });
          }
        } else {
          // Single payment method
          payments = [{ method: payment.payment_method, amount: payment.amount }];
        }
        
        return {
          ...payment,
          transaction_number: payment.receipt_number,
          transaction_type: 'ACCOUNT RECEIPT',
          total: payment.amount,
          created_at: payment.created_at,
          customer_name: payment.customer_name,
          payments: payments
        };
      });
      
      // Merge and sort by time (most recent first)
      const allTransactions = [...salesData, ...formattedPayments].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setSales(allTransactions);
      calculateTotals(salesData, formattedPayments); // Pass both sales and account payments
    } catch (error) {
      console.error(`Error fetching sales (attempt ${attempt}):`, error);
      // Transient Atlas failovers / connection-pool hiccups routinely
      // 500 one call and then succeed on the next. We don't want to
      // scare the cashier with "Failed to load today's sales" on every
      // blip — silently retry up to 3 times with backoff before
      // surfacing an actual toast.
      if (attempt < 3) {
        setTimeout(() => fetchTodaysSales(attempt + 1), attempt * 2000);
        return;
      }
      toast.error("Failed to load today's sales — tap refresh to retry", { duration: 6000 });
    } finally {
      if (attempt === 1 || attempt >= 3) setLoading(false);
    }
  };

  const calculateTotals = (salesData, accountPayments = []) => {
    let cash = 0;
    let card = 0;
    let account = 0;
    let eft = 0;
    let other = 0;
    let refunds = 0;
    // Track refunds by payment method so we can net them into the
    // matching tile (cash refunds reduce Cash, card refunds reduce Card,
    // etc.) — this is what the cashier physically sees in the drawer.
    let cashRefunds = 0;
    let cardRefunds = 0;
    let accountRefunds = 0;
    let eftRefunds = 0;
    let pettyCash = 0;
    let count = 0;

    // Process sales transactions
    salesData.forEach((sale) => {
      const transactionType = (sale.transaction_type || "").toUpperCase();
      const saleTotal = sale.total || 0;

      // Voided sales must be excluded from every tile — they're kept in
      // the list for the audit trail (with a VOIDED badge) but contribute
      // R0 to Cash / Card / Account / EFT / Refunds / Daily Total.
      if (sale.status === "voided" || sale.voided === true || sale.is_voided === true) {
        return;
      }

      // Check if it's a refund - track separately (both aggregate + per-method)
      if (transactionType.includes("REFUND")) {
        refunds += saleTotal;
        if (transactionType.includes("CASH")) cashRefunds += saleTotal;
        else if (transactionType.includes("CARD")) cardRefunds += saleTotal;
        else if (transactionType.includes("ACCOUNT")) accountRefunds += saleTotal;
        else if (transactionType.includes("EFT")) eftRefunds += saleTotal;
        else cashRefunds += saleTotal; // default fallback
        return;
      }
      
      // Check if it's PETTY CASH - track separately (case-insensitive).
      // Petty cash physically leaves the drawer, so it reduces the Cash tile.
      if (transactionType.includes("PETTY")) {
        pettyCash += saleTotal;
        return;
      }

      count++;

      // Determine payment method from the payments array or payment_method field
      // But use the SALE TOTAL, not the tendered amount
      let paymentMethod = "CASH"; // Default
      
      if (sale.payments && Array.isArray(sale.payments) && sale.payments.length > 0) {
        // Get the primary payment method (first one or the one with highest amount)
        const primaryPayment = sale.payments[0];
        paymentMethod = (primaryPayment.method || "CASH").toUpperCase();
        
        // Handle split payments - divide sale total proportionally
        if (sale.payments.length > 1) {
          const totalTendered = sale.payments.reduce((sum, p) => sum + (p.amount || 0), 0);
          sale.payments.forEach((payment) => {
            const method = (payment.method || "CASH").toUpperCase();
            const tenderedAmount = payment.amount || 0;
            // Calculate proportional share of the actual sale total
            const proportion = totalTendered > 0 ? tenderedAmount / totalTendered : 0;
            const actualAmount = saleTotal * proportion;
            
            if (method === "CASH") cash += actualAmount;
            else if (method === "CARD") card += actualAmount;
            else if (method === "ACCOUNT") account += actualAmount;
            else if (method === "EFT") eft += actualAmount;
            else other += actualAmount;
          });
          return; // Already processed split payment
        }
      } else if (sale.payment_method) {
        paymentMethod = sale.payment_method.toUpperCase();
      }

      // Single payment method - use full sale total
      if (paymentMethod === "CASH") cash += saleTotal;
      else if (paymentMethod === "CARD") card += saleTotal;
      else if (paymentMethod === "ACCOUNT") account += saleTotal;
      else if (paymentMethod === "EFT") eft += saleTotal;
      else other += saleTotal;
    });

    // Add account payment receipts to payment method totals
    // (They go into the drawer/terminal but don't count as sales revenue —
    //  the revenue was already recognised when the ACCOUNT SALE happened.
    //  We track the amount separately so we can back it out of DAILY TOTAL
    //  and avoid double-counting.)
    let accountReceiptsIntoTills = 0;
    accountPayments.forEach((payment) => {
      const amount = payment.amount || 0;
      const method = (payment.payment_method || "CASH").toUpperCase();
      
      if (method === "SPLIT" && payment.payments && Array.isArray(payment.payments)) {
        // Handle split account payments
        payment.payments.forEach((pmt) => {
          const pmtAmount = pmt.amount || 0;
          const pmtMethod = (pmt.method || "CASH").toUpperCase();
          if (pmtMethod === "CASH") cash += pmtAmount;
          else if (pmtMethod === "CARD") card += pmtAmount;
          else if (pmtMethod === "EFT") eft += pmtAmount;
          accountReceiptsIntoTills += pmtAmount;
        });
      } else if (method === "CASH") {
        cash += amount;
        accountReceiptsIntoTills += amount;
      } else if (method === "CARD") {
        card += amount;
        accountReceiptsIntoTills += amount;
      } else if (method === "EFT") {
        eft += amount;
        accountReceiptsIntoTills += amount;
      }
    });

    // Physical drawer values — cash refunds physically come out of the
    // drawer, card refunds go back through the terminal, so Cash and Card
    // tiles stay NETTED (matches what the cashier counts at cash-up).
    // Account refunds are ledger-only (no money leaves the drawer / terminal)
    // so the Account tile shows GROSS account sales; the refund is disclosed
    // separately on the Refunds tile and subtracted from Daily Total so the
    // grand total still balances.
    const netCash = cash - cashRefunds - pettyCash;
    const netCard = card - cardRefunds;
    const grossAccount = account;
    const netEft = eft - eftRefunds;

    // Total math is unchanged (still nets ALL refunds), only the Account
    // tile display changes to remove the confusing negative number.
    // Then we subtract account-payment receipts because those amounts are
    // now in the cash/card tiles for cash-up reconciliation but represent
    // debt settlement of a PREVIOUS account sale (already booked as revenue) —
    // if we didn't subtract them, DAILY TOTAL would double-count the money.
    const netTotal = netCash + netCard + (grossAccount - accountRefunds) + netEft + other - accountReceiptsIntoTills;

    setTotals({
      cash: Math.round(netCash * 100) / 100,
      card: Math.round(netCard * 100) / 100,
      account: Math.round(grossAccount * 100) / 100,
      eft: Math.round(netEft * 100) / 100,
      other: Math.round(other * 100) / 100,
      total: Math.round(netTotal * 100) / 100,
      count,
      refunds: Math.round(refunds * 100) / 100,
      pettyCash: Math.round(pettyCash * 100) / 100,
      accountReceiptsIntoTills: Math.round(accountReceiptsIntoTills * 100) / 100,
    });
  };

  const filteredSales = sales.filter((sale) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sale.invoice_number?.toLowerCase().includes(query) ||
      sale.customer_name?.toLowerCase().includes(query) ||
      sale.transaction_type?.toLowerCase().includes(query)
    );
  });

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setShowDetailModal(true);
  };

  const handlePrintReceipt = async (sale) => {
    // Reuse the SAME ReceiptModal that prints at the point of sale so the
    // reprint is an *exact* replica of the original tax invoice — company
    // header/logo, VAT breakdown, line-item table, payment split, PAID stamp,
    // barcode, footer, everything. The old inline HTML window produced a
    // minimal 5-line summary that wasn't a proper tax invoice.
    if (!sale) return;
    // Close the small detail Dialog if it happens to be open (avoids stacking
    // it under the full ReceiptModal).
    setShowDetailModal(false);
    // Re-fetch the full transaction so we're sure to have every field the
    // receipt needs (items, payments, discount lines, VAT breakdown, etc.).
    const key = sale.transaction_number || sale.invoice_number;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales/${encodeURIComponent(key)}`,
      );
      setReprintSale(res.data || sale);
    } catch {
      // Fall back to whatever we already have in the row — better than nothing.
      setReprintSale(sale);
    }
    setShowReprintModal(true);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-ZA", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPaymentMethodBadge = (sale) => {
    if (!sale.payments || sale.payments.length === 0) return null;
    
    // Check if it's a split payment (multiple payment methods)
    if (sale.payments.length > 1) {
      const methods = sale.payments.map(p => {
        const method = (p.method || "").toUpperCase();
        const amount = p.amount || 0;
        return `${method} R${amount.toFixed(0)}`;
      }).join(" + ");
      
      return (
        <Badge className="bg-yellow-100 text-yellow-800 text-xs">
          <CreditCard className="h-3 w-3 mr-1" />
          SPLIT: {methods}
        </Badge>
      );
    }
    
    const mainPayment = sale.payments[0];
    const method = (mainPayment.method || "").toUpperCase();
    
    const badges = {
      CASH: { color: "bg-green-100 text-green-700", icon: Banknote },
      CARD: { color: "bg-blue-100 text-blue-700", icon: CreditCard },
      ACCOUNT: { color: "bg-orange-100 text-orange-700", icon: Building2 },
      EFT: { color: "bg-purple-100 text-purple-700", icon: CreditCard },
    };
    
    const badge = badges[method] || { color: "bg-gray-100 text-gray-700", icon: Receipt };
    const Icon = badge.icon;
    
    return (
      <Badge className={`${badge.color} text-xs`}>
        <Icon className="h-3 w-3 mr-1" />
        {method}
      </Badge>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with totals */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            <h2 className="text-xl font-bold">Today's Sales</h2>
            <Badge variant="secondary" className="bg-white/20 text-white">
              {totals.count} transactions
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchTodaysSales}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Payment method totals */}
        <div className="grid grid-cols-5 gap-2 text-center">
          <div className="bg-white/10 rounded p-2">
            <Banknote className="h-4 w-4 mx-auto mb-1 opacity-70" />
            <p className="text-xs opacity-70">Cash</p>
            <p className="font-bold">R{totals.cash.toFixed(2)}</p>
            {totals.pettyCash > 0 && (
              <p className="text-[10px] text-amber-200 leading-tight" title="Petty cash withdrawn from till">
                − R{totals.pettyCash.toFixed(2)} petty
              </p>
            )}
          </div>
          <div className="bg-white/10 rounded p-2">
            <CreditCard className="h-4 w-4 mx-auto mb-1 opacity-70" />
            <p className="text-xs opacity-70">Card</p>
            <p className="font-bold">R{totals.card.toFixed(2)}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <Building2 className="h-4 w-4 mx-auto mb-1 opacity-70" />
            <p className="text-xs opacity-70">Account</p>
            <p className="font-bold">R{totals.account.toFixed(2)}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <CreditCard className="h-4 w-4 mx-auto mb-1 opacity-70" />
            <p className="text-xs opacity-70">EFT</p>
            <p className="font-bold">R{totals.eft.toFixed(2)}</p>
          </div>
          <div className="bg-white/10 rounded p-2">
            <ArrowDown className={`h-4 w-4 mx-auto mb-1 opacity-70 ${totals.refunds > 0 ? 'text-red-300' : ''}`} />
            <p className="text-xs opacity-70">Refunds</p>
            <p className={`font-bold ${totals.refunds > 0 ? 'text-red-300' : ''}`}>R{totals.refunds.toFixed(2)}</p>
          </div>
        </div>

        {/* Grand total */}
        <div className="mt-3 pt-3 border-t border-white/20 flex justify-between items-center">
          <span className="text-lg">DAILY TOTAL:</span>
          <span className="text-2xl font-bold" data-testid="daily-total-value">R{totals.total.toFixed(2)}</span>
        </div>
        {/* Reconciliation breakdown — surfaces the two "invisible" adjustments
            (petty cash withdrawals and account-payment receipts) that would
            otherwise make the tiles NOT sum to the DAILY TOTAL. Without this,
            a manager doing "cash + card + account" by hand and getting a
            different figure has no idea why. */}
        {(totals.accountReceiptsIntoTills > 0 || totals.pettyCash > 0 || totals.refunds > 0) && (
          <div
            className="mt-2 text-[11px] leading-snug bg-white/10 rounded px-2 py-1.5 text-white/90 space-y-0.5"
            data-testid="daily-total-breakdown"
            title="Explains why Cash + Card + Account may not equal DAILY TOTAL"
          >
            <div className="font-semibold opacity-80">Reconciliation:</div>
            <div>Cash + Card + Account = R{(totals.cash + totals.card + totals.account).toFixed(2)}</div>
            {totals.accountReceiptsIntoTills > 0 && (
              <div>− Account payments received today = <span className="font-semibold">R{totals.accountReceiptsIntoTills.toFixed(2)}</span> <span className="opacity-70">(debt settlement, already booked as revenue on the original sale)</span></div>
            )}
            {totals.refunds > 0 && (
              <div>− Account refunds = R{totals.refunds.toFixed(2)}</div>
            )}
            <div className="pt-0.5 border-t border-white/20 font-semibold">= DAILY TOTAL R{totals.total.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by invoice #, customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Sales list */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Receipt className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>No sales found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSales.map((sale) => {
                const isVoided = sale.status === "voided" || sale.voided === true || sale.is_voided === true;
                return (
                <TableRow 
                  key={sale.id || sale.invoice_number || sale.transaction_number}
                  className={
                    isVoided ? "bg-gray-100 opacity-60" :
                    sale.transaction_type?.includes("REFUND") ? "bg-red-50" : 
                    sale.transaction_type === "ACCOUNT RECEIPT" ? "bg-green-50" : ""
                  }
                  data-testid={isVoided ? `sales-history-voided-${sale.transaction_number || sale.invoice_number}` : undefined}
                >
                  <TableCell className="font-mono text-sm">
                    {formatTime(sale.created_at)}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-blue-600">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={isVoided ? "line-through text-gray-500" : ""}>
                        {sale.invoice_number || sale.transaction_number}
                      </span>
                      {isVoided && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-red-600"
                          title={sale.void_reason || "This sale has been voided"}
                        >
                          VOIDED
                        </span>
                      )}
                      {/* Show a purple "VCH-…" badge on any sale that had a
                          voucher redemption line, so the owner can trace
                          which sale used which voucher without leaving the
                          screen. */}
                      {Array.isArray(sale.items) && sale.items
                        .filter((it) => it?.voucher_code)
                        .slice(0, 2)
                        .map((it, i) => (
                          <span
                            key={`${it.voucher_code}-${i}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-purple-600"
                            title={`Voucher ${it.voucher_code} redeemed on this sale (R${(Number(it.voucher_value) || 0).toFixed(2)})`}
                            data-testid={`sales-history-voucher-badge-${it.voucher_code}`}
                          >
                            VCH-{it.voucher_code.replace(/^VCH-/i, "")}
                          </span>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getPaymentMethodBadge(sale)}
                  </TableCell>
                  <TableCell className="truncate max-w-[150px]">
                    {sale.customer_name || "-"}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${
                    isVoided ? "line-through text-gray-500" :
                    sale.transaction_type?.includes("REFUND") ? "text-red-600" : ""
                  }`}>
                    {sale.transaction_type?.includes("REFUND") ? "-" : ""}
                    R{(sale.total || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewSale(sale)}
                        className="h-7 w-7 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePrintReceipt(sale)}
                        className="h-7 w-7 p-0"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Sale detail modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Sale Details - {selectedSale?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-4">
              {/* Sale info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Date & Time</p>
                  <p className="font-medium">
                    {new Date(selectedSale.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium">{selectedSale.transaction_type}</p>
                </div>
                {selectedSale.customer_name && (
                  <>
                    <div>
                      <p className="text-gray-500">Customer</p>
                      <p className="font-medium">{selectedSale.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Account #</p>
                      <p className="font-medium">{selectedSale.customer_account_number || "-"}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Items */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Items</p>
                <div className="border rounded-lg divide-y max-h-48 overflow-auto">
                  {selectedSale.items?.map((item, idx) => (
                    <div key={idx} className="p-2 flex justify-between text-sm">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {item.quantity} x R{(item.unit_price || 0).toFixed(2)}
                          {item.discount_percent > 0 && ` (-${item.discount_percent}%)`}
                        </p>
                        {item.price_override_reason && (
                          <p className="text-xs text-amber-700" data-testid={`sale-price-override-${idx}`}>
                            Price lowered{item.price_override_original ? ` from R${Number(item.price_override_original).toFixed(2)}` : ""} — {item.price_override_reason}
                          </p>
                        )}
                      </div>
                      <p className="font-medium">
                        R{((item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount_percent || 0) / 100)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>R{(selectedSale.subtotal || 0).toFixed(2)}</span>
                </div>
                {selectedSale.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-R{(selectedSale.discount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>VAT</span>
                  <span>R{(selectedSale.tax || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>R{(selectedSale.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Payments */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Payments</p>
                <div className="space-y-1">
                  {selectedSale.payments?.map((payment, idx) => (
                    <div key={idx} className="flex justify-between text-sm bg-green-50 p-2 rounded">
                      <span>{payment.method}</span>
                      <span className="font-medium">R{(payment.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Refund reason — always show for REFUND transactions so the
                  cashier / owner can see why the money was returned.
                  Falls back to a friendly placeholder for legacy refunds
                  that were rung up before the reason field existed. */}
              {selectedSale.transaction_type?.includes("REFUND") && (
                <div data-testid="sale-details-refund-reason">
                  <p className="text-sm font-medium text-gray-500 mb-2">Refund reason</p>
                  <div className="text-sm bg-red-50 border border-red-200 text-red-900 p-3 rounded whitespace-pre-wrap break-words">
                    {selectedSale.refund_reason?.trim()
                      || <span className="italic text-red-700/70">No reason captured (legacy refund)</span>}
                  </div>
                </div>
              )}

              {/* Void reason — visible on any voided sale so the audit
                  trail is a single click away. */}
              {(selectedSale.status === "voided" || selectedSale.voided === true) && (
                <div data-testid="sale-details-void-reason">
                  <p className="text-sm font-medium text-gray-500 mb-2">Void reason</p>
                  <div className="text-sm bg-gray-100 border border-gray-300 text-gray-800 p-3 rounded whitespace-pre-wrap break-words">
                    {selectedSale.void_reason?.trim()
                      || <span className="italic text-gray-500">No reason captured</span>}
                    {selectedSale.voided_by && (
                      <div className="mt-1 text-xs text-gray-500">
                        Voided by {selectedSale.voided_by}
                        {selectedSale.voided_at ? ` on ${new Date(selectedSale.voided_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sale note (cashier free-text) */}
              {selectedSale.note && !selectedSale.transaction_type?.includes("REFUND") && (
                <div data-testid="sale-details-note">
                  <p className="text-sm font-medium text-gray-500 mb-2">Note</p>
                  <div className="text-sm bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded whitespace-pre-wrap break-words">
                    {selectedSale.note}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handlePrintReceipt(selectedSale)}
                  className="flex-1"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reprint uses the SAME ReceiptModal as the point-of-sale so the copy is
          identical to the original tax invoice. autoPrint=true fires the print
          dialog immediately when the modal opens. */}
      <ReceiptModal
        open={showReprintModal}
        onClose={() => { setShowReprintModal(false); setReprintSale(null); }}
        transaction={reprintSale}
        autoPrint={true}
      />
    </div>
  );
};

export default SalesHistoryPanel;
